import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { ArrowsDownUp, MagnifyingGlass, Plus, PencilSimple, Trash, Eye, Files, CalendarBlank, Buildings, Check, X, Clock, PaperPlaneTilt, FileText, Link, DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default column widths for Transmittals
const DEFAULT_COLUMN_WIDTHS = {
  status: 50,
  transmittal_nr: 200,
  company: 180,
  date: 120,
  response: 50,
  docs: 60,
  subject: 250,
  actions: 120
};

// Sort icon component - moved outside main component
const SortIconComponent = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400 ml-1" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3 w-3 text-primary ml-1" />
    : <ArrowDown className="h-3 w-3 text-primary ml-1" />;
};

// Separate form component to prevent re-renders
const TransmittalFormFields = React.memo(({ formData, onChange }) => {
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Transmittal Number *</Label>
          <Input
            value={formData.transmittal_nr || ''}
            onChange={(e) => handleChange('transmittal_nr', e.target.value)}
            placeholder="e.g., 20151-ILF-TRAN-00002"
            required
          />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={formData.transmittal_type || 'incoming'} onValueChange={(v) => handleChange('transmittal_type', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="incoming">Incoming</SelectItem>
              <SelectItem value="outgoing">Outgoing</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Project</Label>
          <Input
            value={formData.project || ''}
            onChange={(e) => handleChange('project', e.target.value)}
            placeholder="e.g., 20151 MIRAN PIPELINE STUDIES"
          />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={formData.status || 'draft'} onValueChange={(v) => handleChange('status', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Subject</Label>
        <Input
          value={formData.subject || ''}
          onChange={(e) => handleChange('subject', e.target.value)}
          placeholder="Transmittal subject"
        />
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold mb-3">Sender Information</h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Company</Label>
            <Input
              value={formData.sent_by_company || ''}
              onChange={(e) => handleChange('sent_by_company', e.target.value)}
              placeholder="e.g., ILF Consulting Engineers"
            />
          </div>
          <div>
            <Label>Department</Label>
            <Input
              value={formData.sent_by_department || ''}
              onChange={(e) => handleChange('sent_by_department', e.target.value)}
            />
          </div>
          <div>
            <Label>Person</Label>
            <Input
              value={formData.sent_by_person || ''}
              onChange={(e) => handleChange('sent_by_person', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold mb-3">Receiver Information</h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Company</Label>
            <Input
              value={formData.sent_to_company || ''}
              onChange={(e) => handleChange('sent_to_company', e.target.value)}
              placeholder="e.g., Miran Energy Ltd"
            />
          </div>
          <div>
            <Label>Department</Label>
            <Input
              value={formData.sent_to_department || ''}
              onChange={(e) => handleChange('sent_to_department', e.target.value)}
            />
          </div>
          <div>
            <Label>Person</Label>
            <Input
              value={formData.sent_to_person || ''}
              onChange={(e) => handleChange('sent_to_person', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold mb-3">Dates</h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Sent Date</Label>
            <Input
              type="date"
              value={formData.sent_date?.split('T')[0] || ''}
              onChange={(e) => handleChange('sent_date', e.target.value)}
            />
          </div>
          <div>
            <Label>Date Received</Label>
            <Input
              type="date"
              value={formData.date_received?.split('T')[0] || ''}
              onChange={(e) => handleChange('date_received', e.target.value)}
            />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input
              type="date"
              value={formData.due_date?.split('T')[0] || ''}
              onChange={(e) => handleChange('due_date', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div>
        <Label>Remarks</Label>
        <Input
          value={formData.remarks || ''}
          onChange={(e) => handleChange('remarks', e.target.value)}
          placeholder="Additional notes"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.response_required || false}
          onChange={(e) => handleChange('response_required', e.target.checked)}
          className="rounded"
          id="response-required"
        />
        <Label htmlFor="response-required" className="cursor-pointer">Response Required</Label>
      </div>
    </div>
  );
});

TransmittalFormFields.displayName = 'TransmittalFormFields';

const Transmittals = ({ user, selectedProject }) => {
  const [transmittals, setTransmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('incoming');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [linkDocsOpen, setLinkDocsOpen] = useState(false);
  const [selectedTransmittal, setSelectedTransmittal] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [linkedRevisions, setLinkedRevisions] = useState([]);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('transmittal_nr');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  
  const [formData, setFormData] = useState({
    transmittal_nr: '',
    transmittal_type: 'incoming',
    project: '',
    subclass: '',
    subject: '',
    status: 'draft',
    sent_by_company: '',
    sent_by_department: '',
    sent_by_person: '',
    sent_to_company: '',
    sent_to_department: '',
    sent_to_person: '',
    sent_date: '',
    date_received: '',
    due_date: '',
    remarks: '',
    response_required: false
  });
  
  // File upload state
  const [transmittalFile, setTransmittalFile] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, transmittal: null });

  useEffect(() => {
    fetchTransmittals();
  }, [typeFilter, statusFilter, searchTerm, selectedProject]);

  useEffect(() => {
    const handleClick = () => setContextMenu(prev => ({ ...prev, visible: false }));
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const fetchTransmittals = async () => {
    try {
      setLoading(true);
      let url = `${API}/transmittals?`;
      if (typeFilter) url += `transmittal_type=${typeFilter}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (selectedProject) url += `project_id=${selectedProject}&`;
      
      const response = await axios.get(url);
      setTransmittals(response.data);
    } catch (error) {
      toast.error('Failed to fetch transmittals');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevisions = async () => {
    try {
      // Use the new linkable-documents endpoint for better document info
      const response = await axios.get(`${API}/transmittals/linkable-documents`);
      setRevisions(response.data);
    } catch (error) {
      console.error('Failed to fetch linkable documents');
      // Fallback to cycles endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/cycles`);
        setRevisions(fallbackResponse.data);
      } catch (fallbackError) {
        console.error('Failed to fetch revisions');
      }
    }
  };

  const fetchLinkedRevisions = async (transmittalId) => {
    try {
      const response = await axios.get(`${API}/transmittals/${transmittalId}/revisions`);
      setLinkedRevisions(response.data);
    } catch (error) {
      console.error('Failed to fetch linked revisions');
      setLinkedRevisions([]);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = () => {
    setFormData({
      transmittal_nr: '',
      transmittal_type: typeFilter,
      project: '',
      subclass: '',
      subject: '',
      status: 'draft',
      sent_by_company: '',
      sent_by_department: '',
      sent_by_person: '',
      sent_to_company: '',
      sent_to_department: '',
      sent_to_person: '',
      sent_date: '',
      date_received: '',
      due_date: '',
      remarks: '',
      response_required: false
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.transmittal_nr) {
      toast.error('Transmittal number is required');
      return;
    }

    try {
      // Create transmittal first
      const response = await axios.post(`${API}/transmittals`, formData);
      const newTransmittal = response.data;
      
      // Upload file if selected
      if (transmittalFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', transmittalFile);
        await axios.post(`${API}/transmittals/${newTransmittal.id}/upload-file`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Transmittal created successfully');
      setCreateOpen(false);
      setTransmittalFile(null);
      resetForm();
      fetchTransmittals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transmittal');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTransmittal) return;

    try {
      // Update transmittal data
      await axios.put(`${API}/transmittals/${selectedTransmittal.id}`, formData);
      
      // Upload file if selected
      if (transmittalFile) {
        const fileFormData = new FormData();
        fileFormData.append('file', transmittalFile);
        await axios.post(`${API}/transmittals/${selectedTransmittal.id}/upload`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Transmittal updated successfully');
      setEditOpen(false);
      setSelectedTransmittal(null);
      setTransmittalFile(null);
      resetForm();
      fetchTransmittals();
    } catch (error) {
      toast.error('Failed to update transmittal');
    }
  };

  const handleUploadAttachment = async (transmittalId) => {
    // Create a file input and trigger click
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.zip';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      try {
        const fileFormData = new FormData();
        fileFormData.append('file', file);
        await axios.post(`${API}/transmittals/${transmittalId}/upload`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('File uploaded successfully');
        fetchTransmittals();
        if (selectedTransmittal?.id === transmittalId) {
          // Refresh the selected transmittal
          const updated = await axios.get(`${API}/transmittals/${transmittalId}`);
          setSelectedTransmittal(updated.data);
        }
      } catch (error) {
        toast.error('Failed to upload file');
      }
    };
    input.click();
  };

  const handleDelete = async (id, nr) => {
    if (!window.confirm(`Are you sure you want to delete transmittal "${nr}"?`)) return;

    try {
      await axios.delete(`${API}/transmittals/${id}`);
      toast.success('Transmittal deleted');
      fetchTransmittals();
    } catch (error) {
      toast.error('Failed to delete transmittal');
    }
  };

  const handleViewTransmittal = async (transmittal) => {
    setSelectedTransmittal(transmittal);
    await fetchLinkedRevisions(transmittal.id);
    setViewOpen(true);
  };

  const handleDownloadFile = async (transmittal) => {
    if (!transmittal.file_name) {
      toast.error('No file attached to this transmittal');
      return;
    }
    
    try {
      toast.loading('Downloading file...', { id: 'download' });
      const response = await axios.get(`${API}/transmittals/${transmittal.id}/download-file`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', transmittal.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded', { id: 'download' });
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download file', { id: 'download' });
    }
  };

  const handleLinkDocuments = async (transmittal) => {
    setSelectedTransmittal(transmittal);
    await fetchRevisions();
    await fetchLinkedRevisions(transmittal.id);
    setLinkDocsOpen(true);
  };

  const handleLinkRevision = async (revisionId) => {
    if (!selectedTransmittal) return;
    
    try {
      await axios.post(`${API}/transmittals/${selectedTransmittal.id}/link-revision/${revisionId}`);
      toast.success('Document linked to transmittal');
      await fetchLinkedRevisions(selectedTransmittal.id);
      fetchTransmittals();
    } catch (error) {
      toast.error('Failed to link document');
    }
  };

  const handleUnlinkRevision = async (revisionId) => {
    if (!selectedTransmittal) return;
    
    try {
      await axios.delete(`${API}/transmittals/${selectedTransmittal.id}/unlink-revision/${revisionId}`);
      toast.success('Document unlinked from transmittal');
      await fetchLinkedRevisions(selectedTransmittal.id);
      fetchTransmittals();
    } catch (error) {
      toast.error('Failed to unlink document');
    }
  };

  const openEditDialog = (transmittal) => {
    setSelectedTransmittal(transmittal);
    setFormData({
      transmittal_nr: transmittal.transmittal_nr || '',
      transmittal_type: transmittal.transmittal_type || 'incoming',
      project: transmittal.project || '',
      subclass: transmittal.subclass || '',
      subject: transmittal.subject || '',
      status: transmittal.status || 'draft',
      sent_by_company: transmittal.sent_by_company || '',
      sent_by_department: transmittal.sent_by_department || '',
      sent_by_person: transmittal.sent_by_person || '',
      sent_to_company: transmittal.sent_to_company || '',
      sent_to_department: transmittal.sent_to_department || '',
      sent_to_person: transmittal.sent_to_person || '',
      sent_date: transmittal.sent_date || '',
      date_received: transmittal.date_received || '',
      due_date: transmittal.due_date || '',
      remarks: transmittal.remarks || '',
      response_required: transmittal.response_required || false
    });
    setEditOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z').toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'received':
      case 'acknowledged':
        return <Check size={16} className="text-green-600" />;
      case 'sent':
        return <PaperPlaneTilt size={16} className="text-blue-600" />;
      case 'closed':
        return <X size={16} className="text-gray-600" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
      acknowledged: 'bg-purple-100 text-purple-800',
      closed: 'bg-slate-100 text-slate-800'
    };
    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${colors[status] || colors.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Sort handling
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const renderResizableHeader = (columnKey, label, sortKey) => (
    <div className="flex items-center justify-between">
      <button 
        onClick={() => sortKey && handleSort(sortKey)} 
        className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {label} {sortKey && <SortIconComponent column={sortKey} sortColumn={sortColumn} sortDirection={sortDirection} />}
      </button>
      <div
        className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
        onMouseDown={(e) => startResize(columnKey, e)}
      />
    </div>
  );

  const sortedTransmittals = useMemo(() => {
    return [...transmittals].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      if (sortColumn.includes('date') || sortColumn === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transmittals, sortColumn, sortDirection]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="transmittals-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-1">
            Transmittals
          </h1>
          <p className="text-gray-500">
            Manage incoming and outgoing document transmittals
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl h-11 px-5" data-testid="create-transmittal">
              <Plus size={18} className="mr-2" />
              New Transmittal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">Create New Transmittal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <TransmittalFormFields formData={formData} onChange={handleFieldChange} />
              
              {/* File Upload Section */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-sm font-semibold">Attach Transmittal Document (Optional)</Label>
                <div className="mt-2">
                  <Input
                    type="file"
                    onChange={(e) => setTransmittalFile(e.target.files[0])}
                    className="h-10"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.zip"
                    data-testid="transmittal-file-input"
                  />
                  {transmittalFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {transmittalFile.name} ({(transmittalFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
                <Button type="submit" className="bg-gray-900 hover:bg-gray-800 rounded-xl h-10">
                  Create Transmittal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Incoming/Outgoing */}
      <Tabs value={typeFilter} onValueChange={setTypeFilter} className="mb-6">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="incoming" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <ArrowsDownUp size={16} className="rotate-180" />
            Incoming
          </TabsTrigger>
          <TabsTrigger value="outgoing" className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <PaperPlaneTilt size={16} />
            Outgoing
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search transmittals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 rounded-xl border-gray-200"
            />
          </div>
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-10 rounded-xl border-gray-200">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-apple-sm">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left p-3 relative" style={{ width: columnWidths.status }}>
                  <span className="text-sm font-semibold text-gray-700">St.</span>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('status', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.transmittal_nr }}>
                  <button onClick={() => handleSort('transmittal_nr')} className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                    Transmittal Nr <SortIconComponent column="transmittal_nr" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('transmittal_nr', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.company }}>
                  <button onClick={() => handleSort(typeFilter === 'incoming' ? 'sent_by_company' : 'sent_to_company')} className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                    {typeFilter === 'incoming' ? 'Sent By' : 'Sent To'} <SortIconComponent column={typeFilter === 'incoming' ? 'sent_by_company' : 'sent_to_company'} sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('company', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.date }}>
                  <button onClick={() => handleSort(typeFilter === 'incoming' ? 'date_received' : 'sent_date')} className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                    {typeFilter === 'incoming' ? 'Date Received' : 'Sent Date'} <SortIconComponent column={typeFilter === 'incoming' ? 'date_received' : 'sent_date'} sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('date', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.response }}>
                  <span className="text-sm font-semibold text-gray-700">R.</span>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('response', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.docs }}>
                  <button onClick={() => handleSort('nr_of_documents')} className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                    Docs <SortIconComponent column="nr_of_documents" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('docs', e)}
                  />
                </th>
                <th className="text-left p-3 relative" style={{ width: columnWidths.subject }}>
                  <button onClick={() => handleSort('subject')} className="flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900">
                    Subject <SortIconComponent column="subject" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                  <div
                    className="w-1 h-full cursor-col-resize hover:bg-primary/30 absolute right-0 top-0 bottom-0"
                    onMouseDown={(e) => startResize('subject', e)}
                  />
                </th>
              </tr>
            </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center p-8 text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : sortedTransmittals.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center p-8 text-muted-foreground">
                  No transmittals found
                </td>
              </tr>
            ) : (
              sortedTransmittals.map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30 cursor-context-menu" data-testid={`transmittal-row-${t.id}`}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, transmittal: t });
                  }}
                  onDoubleClick={() => handleViewTransmittal(t)}
                >
                  <td className="p-3" style={{ width: columnWidths.status }}>{getStatusIcon(t.status)}</td>
                  <td className="p-3" style={{ width: columnWidths.transmittal_nr }}>
                    <button 
                      className="font-medium text-primary hover:underline text-left truncate block"
                      onClick={() => handleViewTransmittal(t)}
                    >
                      {t.transmittal_nr}
                    </button>
                  </td>
                  <td className="p-3 text-sm truncate" style={{ width: columnWidths.company }}>
                    {typeFilter === 'incoming' ? t.sent_by_company : t.sent_to_company}
                  </td>
                  <td className="p-3 text-sm" style={{ width: columnWidths.date }}>
                    {formatDate(typeFilter === 'incoming' ? t.date_received : t.sent_date)}
                  </td>
                  <td className="p-3" style={{ width: columnWidths.response }}>
                    {t.response_required ? (
                      <span className="text-orange-600 text-xs font-medium">REQ</span>
                    ) : '-'}
                  </td>
                  <td className="p-3" style={{ width: columnWidths.docs }}>
                    <span className="inline-flex items-center gap-1 text-sm">
                      <FileText size={14} />
                      {t.nr_of_documents || 0}
                    </span>
                  </td>
                  <td className="p-3 text-sm truncate" style={{ width: columnWidths.subject }} title={t.subject}>
                    {t.subject || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Right-Click Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed bg-white border rounded-lg shadow-xl py-1 z-50 min-w-[180px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseLeave={() => setContextMenu({ ...contextMenu, visible: false })}
        >
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2" onClick={() => { handleViewTransmittal(contextMenu.transmittal); setContextMenu({ ...contextMenu, visible: false }); }}>
            <Eye size={14} /> View Details
          </button>
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600" onClick={async () => {
            const t = contextMenu.transmittal;
            setContextMenu({ ...contextMenu, visible: false });
            try {
              const res = await axios.get(`${API}/transmittals/${t.id}/download-pdf`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = `${t.transmittal_nr || 'transmittal'}.pdf`; a.click(); window.URL.revokeObjectURL(url);
            } catch(e) { toast.error('Failed to download PDF'); }
          }}>
            <FileText size={14} /> Download PDF
          </button>
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-green-600" onClick={async () => {
            const t = contextMenu.transmittal;
            setContextMenu({ ...contextMenu, visible: false });
            try {
              const res = await axios.get(`${API}/transmittals/${t.id}/download-excel`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = `${t.transmittal_nr || 'transmittal'}.xlsx`; a.click(); window.URL.revokeObjectURL(url);
            } catch(e) { toast.error('Failed to download Excel'); }
          }}>
            <DownloadSimple size={14} /> Download Excel
          </button>
          {contextMenu.transmittal?.file_name && (
            <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-blue-600" onClick={() => { handleDownloadFile(contextMenu.transmittal); setContextMenu({ ...contextMenu, visible: false }); }}>
              <DownloadSimple size={14} /> Download Original
            </button>
          )}
          <div className="border-t my-1" />
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2" onClick={() => { handleLinkDocuments(contextMenu.transmittal); setContextMenu({ ...contextMenu, visible: false }); }}>
            <Link size={14} /> Link Documents
          </button>
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2" onClick={() => { openEditDialog(contextMenu.transmittal); setContextMenu({ ...contextMenu, visible: false }); }}>
            <PencilSimple size={14} /> Edit
          </button>
          <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600" onClick={() => { handleDelete(contextMenu.transmittal.id, contextMenu.transmittal.transmittal_nr); setContextMenu({ ...contextMenu, visible: false }); }}>
            <Trash size={14} /> Delete
          </button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={(open) => {
        setEditOpen(open);
        if (!open) {
          setSelectedTransmittal(null);
          setTransmittalFile(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Transmittal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <TransmittalFormFields formData={formData} onChange={handleFieldChange} />
            
            {/* File Upload/Replace Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-semibold">Attachment</Label>
              {selectedTransmittal?.file_name && (
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg mt-2 mb-2">
                  <FileText size={18} className="text-blue-600" />
                  <span className="text-sm flex-1">{selectedTransmittal.file_name}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadFile(selectedTransmittal)}
                  >
                    <DownloadSimple size={14} />
                  </Button>
                </div>
              )}
              <div className="mt-2">
                <Input
                  type="file"
                  onChange={(e) => setTransmittalFile(e.target.files[0])}
                  className="h-10"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.zip"
                />
                {transmittalFile && (
                  <p className="text-xs text-green-600 mt-1">
                    New file selected: {transmittalFile.name} ({(transmittalFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                {!selectedTransmittal?.file_name && !transmittalFile && (
                  <p className="text-xs text-gray-400 mt-1">No file attached</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
              <Button type="submit" className="bg-primary">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Transmittal Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt size={20} />
              Transmittal: {selectedTransmittal?.transmittal_nr}
            </DialogTitle>
          </DialogHeader>
          {selectedTransmittal && (
            <div className="space-y-6">
              {/* Transmittal Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{selectedTransmittal.transmittal_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(selectedTransmittal.status)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">From</p>
                  <p className="font-medium">{selectedTransmittal.sent_by_company || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">To</p>
                  <p className="font-medium">{selectedTransmittal.sent_to_company || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sent Date</p>
                  <p>{formatDate(selectedTransmittal.sent_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Received Date</p>
                  <p>{formatDate(selectedTransmittal.date_received)}</p>
                </div>
                {selectedTransmittal.subject && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Subject</p>
                    <p>{selectedTransmittal.subject}</p>
                  </div>
                )}
                {/* Attachment Section */}
                <div className="col-span-2 border-t pt-3 mt-2">
                  <p className="text-xs text-muted-foreground mb-2">Attachment</p>
                  {selectedTransmittal.file_name ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <FileText size={24} className="text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{selectedTransmittal.file_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {selectedTransmittal.file_size ? `${(selectedTransmittal.file_size / 1024).toFixed(1)} KB` : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleDownloadFile(selectedTransmittal)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <DownloadSimple size={16} className="mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUploadAttachment(selectedTransmittal.id)}
                      >
                        <UploadSimple size={16} className="mr-1" />
                        Replace
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-muted-foreground italic flex-1">No file attached</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUploadAttachment(selectedTransmittal.id)}
                      >
                        <UploadSimple size={16} className="mr-1" />
                        Upload File
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Linked Documents */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <FileText size={18} />
                    Documents in this Transmittal ({linkedRevisions.length})
                  </h4>
                  <Button size="sm" variant="outline" onClick={() => handleLinkDocuments(selectedTransmittal)}>
                    <Link size={14} className="mr-1" />
                    Manage Documents
                  </Button>
                </div>
                
                {linkedRevisions.length === 0 ? (
                  <div className="text-center p-6 bg-muted/30 rounded-sm text-muted-foreground">
                    No documents linked to this transmittal yet
                  </div>
                ) : (
                  <table className="w-full border border-border rounded-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 text-xs font-semibold">Document</th>
                        <th className="text-left p-2 text-xs font-semibold">Revision</th>
                        <th className="text-left p-2 text-xs font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedRevisions.map((rev) => (
                        <tr key={rev.id} className="border-t border-border">
                          <td className="p-2 text-sm">{rev.document_name || rev.document_id}</td>
                          <td className="p-2 text-sm">{rev.revision}</td>
                          <td className="p-2 text-sm capitalize">{rev.review_status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Documents Dialog */}
      <Dialog open={linkDocsOpen} onOpenChange={setLinkDocsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Link Documents to: {selectedTransmittal?.transmittal_nr}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Currently Linked */}
            <div>
              <h4 className="font-semibold mb-2 text-sm">Linked Documents ({linkedRevisions.length})</h4>
              {linkedRevisions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No documents linked yet</p>
              ) : (
                <div className="space-y-2">
                  {linkedRevisions.map((rev) => (
                    <div key={rev.id} className="flex items-center justify-between p-3 bg-green-50 rounded-sm border border-green-200">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{rev.document_number || 'No Number'}</p>
                        <p className="text-sm text-gray-700">{rev.title || rev.document_name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">Rev: {rev.revision || '-'}</span>
                          {rev.review_status && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {rev.review_status}
                            </span>
                          )}
                          {rev.created_at && (
                            <span className="text-xs text-muted-foreground">
                              Uploaded: {formatDate(rev.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleUnlinkRevision(rev.id)} className="text-red-600">
                        <X size={14} className="mr-1" /> Unlink
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available to Link */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-2 text-sm">Available Documents</h4>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {revisions
                  .filter(r => !linkedRevisions.find(lr => lr.id === r.id))
                  .map((rev) => (
                    <div key={rev.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-sm border border-gray-200 hover:bg-muted/50">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{rev.document_number || 'No Number'}</p>
                        <p className="text-sm text-gray-700">{rev.title || rev.document_name}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">Rev: {rev.revision || '-'}</span>
                          {rev.type === 'cycle' && rev.review_status && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {rev.review_status}
                            </span>
                          )}
                          {rev.discipline && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                              {rev.discipline}
                            </span>
                          )}
                          {rev.created_at && (
                            <span className="text-xs text-muted-foreground">
                              Uploaded: {formatDate(rev.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleLinkRevision(rev.id)}>
                        <Link size={14} className="mr-1" /> Link
                      </Button>
                    </div>
                  ))}
                {revisions.filter(r => !linkedRevisions.find(lr => lr.id === r.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No documents available to link</p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Transmittals;
